import React, { useEffect, useState, useRef, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Button,
  LinearProgress,
  Tooltip,
  TooltipProps,
  linearProgressClasses,
  tooltipClasses,
} from '@mui/material';
import { styled } from '@mui/system';
import { cloneDeep, isEmpty, round } from 'lodash';
import PowerChart from './power-chart';

import Color from '@tool/color';
import {
  CloseIcon20,
  UpIcon,
  DownIcon,
  DownChangeIcon,
  UpChangeIcon,
} from '@styled/icon';
import { newRate } from '@tool/index';
import moUser from '@/store/user';
import moIndustry from '../../store/industry';
import HRService from '@/service/hr';
import Empty from '@/component/empty';

interface IProps {}
interface IState {}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    padding: '10px 20px',
    '& p': {
      color: Color.white,
      display: 'flex',
      justifyContent: 'space-between',
      width: '200px',
      lineHeight: '34px',
      fontWeight: 600,
      '& label': {
        fontFamily: 'Mont SemiBold',
      },
      '& span': {
        color: Color.whiteColor(0.7),
      },
    },
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor:
      theme.palette.mode === 'light' ? Color.secondary : Color.ec,
  },
}));

const Manpower = (props: IProps, state: IState) => {
  const navi = useNavigate();
  const { powerList } = moIndustry;

  const changeUrl = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          powerList?.salary_change_trend?.industry_lv2 ||
          powerList?.dimission_rate?.industry_lv1,
      });
    }
    window.location.href = '/index/industry-research/0';
  };

  const goUpSalary = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          powerList?.dimission_rate?.industry_lv2 ||
          powerList?.dimission_rate?.industry_lv1,
      });
    }
    window.location.href = '/index/human-indicator/1?id=1';
  };

  const goLeaveSalary = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          powerList?.dimission_rate?.industry_lv2 ||
          powerList?.dimission_rate?.industry_lv1,
      });
    }
    window.location.href = '/index/human-indicator/2?id=2';
  };

  const goFreshmenSalary = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          powerList?.fresh_graduate_salary?.industry_lv2 ||
          powerList?.dimission_rate?.industry_lv1,
      });
    }
    window.location.href = '/index/human-indicator/3?id=3';
  };

  const goCitySlary = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await HRService.editUserOption({
        my_industry:
          powerList?.city_difference?.industry_lv2 ||
          powerList?.dimission_rate?.industry_lv1,
      });
    }
    window.location.href = '/index/human-indicator/4?id=4';
  };

  const getMax = useMemo(() => {
    const result: any = cloneDeep(powerList);
    const list: any = result?.industry_salary_change_rank?.sort(
      (a: any, b: any) => b.change_rate - a.change_rate,
    );
    return !isEmpty(list) ? list[0].change_rate : 100;
  }, [powerList]);

  useEffect(() => {
    return () => {};
  }, []);

  return (
    <section className="dash-effect-warp">
      <section className="dash-industry-content">
        <h3 className="top">人力核心指标 - 人才指标</h3>
        <section className="contents">
          <section className="left dash-move" onClick={goUpSalary}>
            <h4>涨薪率</h4>
            <PowerChart />
          </section>
          <section className="mid dash-move" onClick={goLeaveSalary}>
            <h4>
              离职率
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {powerList?.dimission_rate?.industry_lv2 ||
                        powerList?.dimission_rate?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{powerList?.dimission_rate?.year}</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {powerList?.dimission_rate?.rate ||
                powerList?.dimission_rate?.rate === 0 ?
                  `${round(powerList?.dimission_rate?.rate, 1)}%` :
                  '-'}
              </b>
            </HtmlTooltip>

            <section className="change-radio">
              <a>较上期变化</a>
              {powerList?.dimission_rate?.change_rate ||
              powerList?.dimission_rate?.change_rate === 0 ? (
                  <span>
                    {powerList?.dimission_rate?.change_rate >= 0 ? (
                      <UpChangeIcon />
                    ) : (
                      <DownChangeIcon />
                    )}
                  &nbsp;
                    <i
                      style={{
                        color:
                        powerList?.dimission_rate?.change_rate >= 0 ?
                          Color.red :
                          Color.success,
                      }}>
                      {round(Math.abs(powerList?.dimission_rate?.change_rate), 1)}
                    %
                    </i>
                  </span>
                ) : (
                  '-'
                )}
            </section>
          </section>
          <section className="mid dash-move" onClick={goFreshmenSalary}>
            <h4>
              应届生起薪 - 本科
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {powerList?.fresh_graduate_salary?.industry_lv2 ||
                        powerList?.fresh_graduate_salary?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{powerList?.fresh_graduate_salary?.year}</label>
                  </p>
                  <p>
                    <span>地区</span>
                    <span>{powerList?.fresh_graduate_salary?.city}</span>
                  </p>
                </React.Fragment>
              }>
              <b>
                {powerList?.fresh_graduate_salary?.bachelor ||
                powerList?.fresh_graduate_salary?.bachelor === 0 ?
                  round(
                    powerList?.fresh_graduate_salary?.bachelor,
                    1,
                  ).toLocaleString() :
                  '-'}
              </b>
            </HtmlTooltip>

            <section className="change-radio">
              <a>较上期变化</a>
              {powerList?.fresh_graduate_salary?.bachelor_change_rate ||
              powerList?.fresh_graduate_salary?.bachelor_change_rate === 0 ? (
                  <span>
                    {powerList?.fresh_graduate_salary?.bachelor_change_rate >=
                  0 ? (
                        <UpChangeIcon />
                      ) : (
                        <DownChangeIcon />
                      )}
                  &nbsp;
                    <i
                      style={{
                        color:
                        powerList?.fresh_graduate_salary
                          ?.bachelor_change_rate >= 0 ?
                          Color.red :
                          Color.success,
                      }}>
                      {round(
                        Math.abs(
                          powerList?.fresh_graduate_salary?.bachelor_change_rate,
                        ) * 100,
                        1,
                      )}
                    %
                    </i>
                  </span>
                ) : (
                  '-'
                )}
            </section>
          </section>
          <section className="mid dash-move" onClick={goCitySlary}>
            <h4>
              城市薪酬差异系数
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {powerList?.city_difference?.industry_lv2 ||
                        powerList?.city_difference?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>基准城市</span>
                    <label>北京</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {powerList?.city_difference?.diff_index ||
                powerList?.city_difference?.diff_index === 0 ?
                  round(
                    powerList?.city_difference?.diff_index,
                    1,
                  ).toLocaleString() :
                  '-'}
              </b>
            </HtmlTooltip>
            <section className="citys">
              <a>当前城市</a>
              <span>{powerList?.city_difference?.city}</span>
            </section>
          </section>
          <section
            className="right dash-move common-progress"
            onClick={goUpSalary}>
            <h4>
              行业涨薪率排行
              {/* &nbsp;<span>new</span> */}
            </h4>
            {!isEmpty(powerList?.industry_salary_change_rank) ? (
              <section style={{ width:'100%' }}>
                {powerList?.industry_salary_change_rank?.map(
                  (res: any, r: any) => {
                    return (
                      <section key={r} className="progress-item">
                        <b>{r + 1}</b>
                        <section className="liner-show">
                          <p>{res.industry_lv2 || res.industry_lv1}</p>
                          <BorderLinearProgress
                            variant="determinate"
                            value={(res?.change_rate / getMax) * 100}/>
                        </section>
                        <span className="numbers" style={{ minWidth:'30px' }}>
                          {res.change_rate ?
                            `${round(res?.change_rate * 100, 1)}%` :
                            ''}
                        </span>
                      </section>
                    );
                  },
                )}
              </section>
            ) : (
              <Empty height="auto" />
            )}
          </section>
        </section>
      </section>

      {!moUser.isLogin && (
        <section className="position-title">
          全行业数据 - 登录体验更细分维度数据
        </section>
      )}
      {moUser.isIndicatorExpired && (
        <section className="position-title">
          全行业数据 - 开通会员，支持所有细分行
        </section>
      )}
    </section>
  );
};

export default observer(Manpower);
